import { IUser, IUsersResponse, ICreateUserData } from "../../types/index";
import { apiClient } from "../core/apiClient";


export interface StudentSearchResult {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  rut: string;
  activo: boolean;
}

export interface FamilyRelation {
  id: string;
  apoderado_id: string;
  estudiante_id: string;
  tipo_relacion: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  apoderado?: {
    id: string;
    nombre: string;
    apellido: string;
    email: string;
    rut: string;
  };
  estudiante?: {
    id: string;
    nombre: string;
    apellido: string;
    email: string;
    rut: string;
  };
}

export interface ApoderadoChildrenResponse {
  apoderado_id: string;
  children: FamilyRelation[];
}

/**
 * Interfaz para las estadísticas del usuario
 */
export interface UserStats {
  packs: number;
  productosAsignados: number;
  pedidosProgramados: number;
  pedidosParaHoy: number;
}

/**
 * Interfaz para un pedido del usuario
 */
export interface UserOrder {
  id: string;
  fecha: string;
  fechaOriginal: string; // Para ordenamiento
  productos: string[];
  estado: string;
  esParaHoy: boolean;
  total: number;
  fechaRetiro?: string;
  isCreditOrder?: boolean;
}

export interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  price: number;
  total: number;
}

/**
 * Servicio para gestión de usuarios
 *
 * Maneja todas las operaciones relacionadas con usuarios:
 * - Búsqueda y obtención de usuarios
 * - Creación de nuevos usuarios
 * - Gestión de perfiles
 * - Relaciones familiares
 */
class UserService {
  /**
   * Obtiene todos los usuarios del sistema
   *
   * @returns Promise con la lista de usuarios
   */
  static async getAllUsers(): Promise<IUsersResponse> {
    const pageSize = 1000; // backend allows up to 1000 per request
    let offset = 0;
    let allUsers: IUser[] = [];
    let total = 0;
    let message = "";

    // Fetch in pages until we gather all users reported by backend
    // Protect against infinite loops if backend misreports total
    const maxIterations = 100; // safety cap
    let iterations = 0;
    while (iterations < maxIterations) {
      const resp = await apiClient.get<{
        success: boolean;
        data: { users: IUser[]; total: number };
        message: string;
      }>(`/api/v1/users`, { params: { limit: pageSize, offset } });

      const { users, total: reportedTotal } = resp.data.data;
      total = reportedTotal;
      message = resp.data.message || message;

      if (!Array.isArray(users) || users.length === 0) {
        break;
      }

      allUsers = allUsers.concat(users);
      offset += users.length;

      if (allUsers.length >= total) {
        break;
      }

      iterations += 1;
    }

    return { users: allUsers, total, message };
  }

  /**
   * Obtiene un usuario específico por ID
   *
   * @param userId - ID del usuario a obtener
   * @returns Promise con el usuario encontrado
   */
  static async getUserById(userId: string): Promise<IUser> {
    const resp = await apiClient.get<{
      success: boolean;
      data: IUser;
    }>(`/api/v1/users/${userId}`);
    return resp.data.data;
  }

  /**
   * Busca usuarios por término de búsqueda
   *
   * @param searchTerm - Término de búsqueda
   * @returns Promise con los usuarios que coinciden
   */
  static async searchUsers(searchTerm: string): Promise<IUsersResponse> {
    const pageSize = 1000;
    let offset = 0;
    let allUsers: IUser[] = [];
    let total = 0;
    let message = "";
    const maxIterations = 100;
    let iterations = 0;

    while (iterations < maxIterations) {
      const resp = await apiClient.get<{
        success: boolean;
        data: { users: IUser[]; total: number };
        message: string;
      }>(`/api/v1/users`, { params: { q: searchTerm, limit: pageSize, offset } });

      const { users, total: reportedTotal } = resp.data.data;
      total = reportedTotal;
      message = resp.data.message || message;

      if (!Array.isArray(users) || users.length === 0) break;

      allUsers = allUsers.concat(users);
      offset += users.length;
      if (allUsers.length >= total) break;
      iterations += 1;
    }

    return { users: allUsers, total, message };
  }

  /**
   * Busca estudiantes por nombre
   *
   * @param name - Nombre del estudiante a buscar
   * @returns Promise con los estudiantes que coinciden
   */
  static async searchStudentsByName(
    name: string
  ): Promise<StudentSearchResult[]> {
    const resp = await apiClient.get<{
      success: boolean;
      data: StudentSearchResult[];
    }>(`/api/v1/users/students/search`, { params: { name } });
    return resp.data.data;
  }

  /**
   * Crea un nuevo usuario
   *
   * @param userData - Datos del usuario a crear
   * @returns Promise con el usuario creado
   */
  static async createUser(userData: ICreateUserData): Promise<IUser> {
    const resp = await apiClient.post<{
      success: boolean;
      data: IUser;
    }>(`/api/v1/users`, userData);
    return resp.data.data;
  }

  /**
   * Actualiza un usuario existente
   *
   * @param userId - ID del usuario a actualizar
   * @param userData - Datos a actualizar
   * @returns Promise con el usuario actualizado
   */
  static async updateUser(
    userId: string,
    userData: Partial<IUser> | ICreateUserData
  ): Promise<IUser> {
    const payload: any = {
      nombres: (userData as any).nombres,
      apellidos: (userData as any).apellidos,
      rut: (userData as any).rut,
      email: (userData as any).email,
      password: (userData as any).password,
      roles: (userData as any).roles,
      activo: (userData as any).activo,
    };
    const resp = await apiClient.put<{
      success: boolean;
      data: IUser;
    }>(`/api/v1/users/${userId}`, payload);
    return resp.data.data;
  }

  /**
   * Elimina un usuario
   *
   * @param userId - ID del usuario a eliminar
   * @returns Promise con el resultado de la eliminación
   */
  static async deleteUser(
    userId: string
  ): Promise<{ success: boolean; message: string }> {
    await apiClient.delete(`/api/v1/users/${userId}`);
    return { success: true, message: "Usuario eliminado exitosamente" };
  }

  /**
   * Busca un usuario por RUT
   *
   * @param rut - RUT del usuario a buscar
   * @returns Promise con el usuario encontrado o null
   */
  static async findUserByRut(rut: string): Promise<IUser | null> {
    try {
      const response = await apiClient.get(`/users/search?rut=${rut}`);
      return response.data.data || null;
    } catch (error) {
      console.error("Error buscando usuario por RUT:", error);
      return null;
    }
  }

  /**
   * Obtiene estadísticas de usuarios
   *
   * @returns Promise con las estadísticas de usuarios
   */
  static async getUserStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    byRole: Record<string, number>;
  }> {
    const resp = await apiClient.get<{
      success: boolean;
      data: { total: number; active: number; inactive: number; byRole: Record<string, number> };
    }>(`/api/v1/users/stats`);
    return resp.data.data;
  }

  // ============================================================================
  // NUEVOS MÉTODOS PARA RELACIONES FAMILIARES
  // ============================================================================

  /**
   * Obtiene los hijos de un apoderado
   *
   * @param apoderadoId - ID del apoderado
   * @returns Promise con los hijos del apoderado
   */
  static async getApoderadoChildren(
    // apoderadoId: string
  ): Promise<ApoderadoChildrenResponse> {
    try {
      // Usar el endpoint correcto que existe en el backend
      const response = await apiClient.get<{
        success: boolean;
        data: ApoderadoChildrenResponse;
      }>(`/api/v1/parents/students`);

      return response.data.data;
    } catch (error: any) {
      console.error("Error obteniendo hijos del apoderado:", error);
      throw new Error("Error al obtener los hijos del apoderado");
    }
  }

  /**
   * Obtiene las relaciones familiares de un estudiante
   *
   * @param estudianteId - ID del estudiante
   * @returns Promise con las relaciones familiares
   */
  static async getStudentFamilyRelations(
    estudianteId: string
  ): Promise<FamilyRelation[]> {
    try {
      const response = await apiClient.get<FamilyRelation[]>(
        `/api/v1/family-relations/estudiante/${estudianteId}`
      );

      return response.data;
    } catch (error: any) {
      console.error(
        "Error obteniendo relaciones familiares del estudiante:",
        error
      );
      throw new Error(
        "Error al obtener las relaciones familiares del estudiante"
      );
    }
  }

  /**
   * Busca estudiantes por apoderado
   *
   * @param apoderadoId - ID del apoderado
   * @returns Promise con los estudiantes asociados
   */
  static async searchStudentsByApoderado(
    apoderadoId: string
  ): Promise<StudentSearchResult[]> {
    try {
      const response = await apiClient.get<StudentSearchResult[]>(
        `/api/v1/family-relations/apoderado/${apoderadoId}/students`
      );

      return response.data;
    } catch (error: any) {
      console.error("Error buscando estudiantes por apoderado:", error);
      throw new Error("Error al buscar estudiantes del apoderado");
    }
  }

  /**
   * Crea una nueva relación familiar
   *
   * @param apoderadoId - ID del apoderado
   * @param estudianteId - ID del estudiante
   * @param tipoRelacion - Tipo de relación (padre, madre, tutor, etc.)
   * @returns Promise con la relación creada
   */
  static async createFamilyRelation(
    apoderadoId: string,
    estudianteId: string,
    tipoRelacion: string
  ): Promise<FamilyRelation> {
    try {
      const response = await apiClient.post<FamilyRelation>(
        `/api/v1/family-relations/`,
        {
          apoderado_id: apoderadoId,
          estudiante_id: estudianteId,
          tipo_relacion: tipoRelacion,
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error creando relación familiar:", error);
      throw new Error("Error al crear la relación familiar");
    }
  }

  /**
   * Elimina una relación familiar
   *
   * @param relationId - ID de la relación a eliminar
   * @returns Promise con el resultado de la eliminación
   */
  static async deleteFamilyRelation(
    relationId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      await apiClient.delete(`/api/v1/family-relations/${relationId}`);

      return {
        success: true,
        message: "Relación familiar eliminada exitosamente",
      };
    } catch (error: any) {
      console.error("Error eliminando relación familiar:", error);
      throw new Error("Error al eliminar la relación familiar");
    }
  }

  /**
   * Obtiene todos los roles del sistema
   */
  static async getRoles(): Promise<string[]> {
    const resp = await apiClient.get<{
      success: boolean;
      data: string[];
    }>(`/api/v1/users/roles`);
    return resp.data.data;
  }

  // ============================================================================
  // MÉTODOS DE PEDIDOS - Reutilizables para cualquier usuario
  // ============================================================================

  /**
   * Obtiene las estadísticas del usuario para el dashboard
   */
  static async getUserDashboardStats(): Promise<UserStats> {
    try {
      // Obtener packs disponibles
      const packsResponse = await apiClient.get<{ success: boolean; data: { availablePacks: number } }>(
        `/api/v1/parents/available-packs`
      );

      // Obtener historial de pedidos para calcular estadísticas
      const ordersResponse = await apiClient.get<{ success: boolean; data: any[] }>(
        `/api/v1/parents/my/order-history`
      );

      const orders = ordersResponse.data.data || [];
      const today = new Date().toISOString().split('T')[0];

      // Calcular estadísticas
      const pedidosParaHoy = orders.filter((order: any) => 
        order.fecha && order.fecha.startsWith(today)
      ).length;

      const pedidosProgramados = orders.filter((order: any) => 
        order.fecha && order.fecha > today
      ).length;

      // Obtener productos asignados - contar asignaciones únicas, no productos en pedidos
      const productosAsignados = orders.length; // Cada pedido representa una asignación

      return {
        packs: packsResponse.data.data.availablePacks || 0,
        productosAsignados,
        pedidosProgramados,
        pedidosParaHoy,
      };
    } catch (error: any) {
      console.error("Error obteniendo estadísticas del usuario:", error);
      // Retornar datos mock en caso de error
      return {
        packs: 0,
        productosAsignados: 0,
        pedidosProgramados: 0,
        pedidosParaHoy: 0,
      };
    }
  }

  /**
   * Obtiene los pedidos del usuario
   */
  static async getUserOrders(): Promise<UserOrder[]> {
    try {
      const response = await apiClient.get<{ success: boolean; data: any[] }>(
        `/api/v1/parents/my/order-history`
      );

      const orders = response.data.data || [];
      const today = new Date().toISOString().split('T')[0];

      return orders.map((order: any) => {
        // Manejar la fecha correctamente
        let fechaFormateada = 'Fecha no disponible';
        let fechaOriginal = order.fecha;
        
        if (order.fecha) {
          try {
            const fecha = new Date(order.fecha);
            if (!isNaN(fecha.getTime())) {
              fechaFormateada = fecha.toLocaleDateString('es-CL', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              });
              fechaOriginal = order.fecha;
            }
          } catch (error) {
            console.error('Error formateando fecha:', order.fecha, error);
          }
        }
        
        return {
          id: order.id?.replace('pedido_', '') || order.id || 'sin-id',
          fecha: fechaFormateada,
          fechaOriginal: fechaOriginal,
          productos: order.productos ? order.productos : (order.producto ? [order.producto] : ['Producto Variado']),
          estado: order.estado || 'Programado',
          esParaHoy: order.fecha && order.fecha.startsWith(today),
          total: order.monto || 0,
          fechaRetiro: order.fecha,
          isCreditOrder: order.esCreditOrder,
        };
      });
    } catch (error: any) {
      console.error("Error obteniendo pedidos del usuario:", error);
      return [];
    }
  }

  /**
   * Obtiene los packs disponibles del usuario
   */
  static async getAvailablePacks(): Promise<any[]> {
    try {
      const response = await apiClient.get<{ success: boolean; data: any[] }>(
        `/api/v1/parents/available-packs`
      );

      return response.data.data || [];
    } catch (error: any) {
      console.error("Error obteniendo packs disponibles:", error);
      return [];
    }
  }
}

export default UserService;
